import Decimal from 'decimal.js';
import type { BillingPeriodUnit, Money } from './contract';
import { currentLocaleTag, tr } from '../i18n';

const MoneyDecimal = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

const periodUnitKeys: Record<BillingPeriodUnit, [
  'period.minute.one' | 'period.day.one' | 'period.week.one' | 'period.month.one' | 'period.year.one',
  'period.minute.other' | 'period.day.other' | 'period.week.other' | 'period.month.other' | 'period.year.other',
]> = {
  MINUTE: ['period.minute.one', 'period.minute.other'],
  DAY: ['period.day.one', 'period.day.other'],
  WEEK: ['period.week.one', 'period.week.other'],
  MONTH: ['period.month.one', 'period.month.other'],
  YEAR: ['period.year.one', 'period.year.other'],
};

export function periodSuffix(unit: BillingPeriodUnit, count: number): string {
  const [singularKey, pluralKey] = periodUnitKeys[unit];
  return count === 1
    ? tr('period.single', { unit: tr(singularKey) })
    : tr('period.every', { count, unit: tr(pluralKey) });
}

export function formatMoney(money: Money): string {
  let amount: Decimal;
  try {
    amount = decimalAmount(money);
  } catch {
    return `${money.currency} ${money.amount}`;
  }
  const digits = currencyDigits(money.currency);
  const rounded = amount.toDecimalPlaces(digits, Decimal.ROUND_HALF_UP);
  const [integer, fraction = ''] = rounded.abs().toFixed(digits).split('.');
  const whole = BigInt(integer!);
  // BigInt preserves large integer amounts; only the fractional part is substituted.
  const signedWhole = rounded.isNegative() && !rounded.isZero() ? (whole === 0n ? -0 : -whole) : whole;
  try {
    return new Intl.NumberFormat(currentLocaleTag(), {
      style: 'currency', currency: money.currency,
      minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).formatToParts(signedWhole)
      .map((part) => part.type === 'fraction' ? fraction : part.value)
      .join('');
  } catch {
    return `${money.currency} ${rounded.toFixed(digits)}`;
  }
}

const ZERO_DECIMAL_CURRENCIES = new Set(['CLP', 'JPY', 'PYG']);

function currencyDigits(currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase()) ? 0 : 2;
}

function decimalAmount(money: Money): Decimal {
  const amount = new MoneyDecimal(money.amount);
  if (!amount.isFinite()) throw new RangeError('Valor monetário deve ser finito');
  return amount;
}

function ledgerAmount(amount: Decimal, currency: string): Money {
  return { amount: amount.toFixed(3, Decimal.ROUND_HALF_UP), currency };
}

export function roundToCurrency(money: Money): Money {
  return ledgerAmount(decimalAmount(money).toDecimalPlaces(currencyDigits(money.currency), Decimal.ROUND_HALF_UP), money.currency);
}

export function moneyToMinorUnits(money: Money): number {
  const units = MoneyDecimal.max(0, decimalAmount(money))
    .mul(new MoneyDecimal(10).pow(currencyDigits(money.currency)))
    .toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  if (units.gt(Number.MAX_SAFE_INTEGER)) {
    throw new RangeError('Valor monetário excede o limite seguro do pagamento');
  }
  return units.toNumber();
}

export function sumMoney(items: Money[]): Money | null {
  if (items.length === 0) return null;
  const first = items[0];
  if (!first) return null;
  const currency = first.currency;
  let total = new MoneyDecimal(0);
  for (const item of items) {
    if (item.currency !== currency) {
      throw new Error('Não é possível somar moedas diferentes');
    }
    total = total.plus(decimalAmount(item));
  }
  return ledgerAmount(total, currency);
}

export function applyPercentDiscount(money: Money, percent: number): Money {
  return ledgerAmount(
    decimalAmount(money).minus(decimalAmount(money).mul(percent).div(100).toDecimalPlaces(3, Decimal.ROUND_HALF_UP)),
    money.currency,
  );
}

export function subtractMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) {
    throw new Error('Não é possível subtrair moedas diferentes');
  }
  return ledgerAmount(decimalAmount(a).minus(decimalAmount(b)), a.currency);
}

export function minMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) {
    throw new Error('Não é possível comparar moedas diferentes');
  }
  return decimalAmount(a).lte(decimalAmount(b)) ? a : b;
}

export function moneyEquals(a: Money, b: Money): boolean {
  if (a.currency !== b.currency) return false;
  try {
    return decimalAmount(a).eq(decimalAmount(b));
  } catch {
    return false;
  }
}

export function installmentLabel(money: Money, maxInstallments: number): string | null {
  let amount: Decimal;
  try {
    amount = decimalAmount(money);
  } catch {
    return null;
  }
  if (!Number.isSafeInteger(maxInstallments) || maxInstallments < 2) return null;
  const formatted = formatMoney(ledgerAmount(amount.div(maxInstallments), money.currency));
  return tr('installment.label', { count: maxInstallments, value: formatted });
}
